import React from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  TrendingUp, ChevronDown, ChevronUp,
  CheckSquare, Square, CheckCircle,
} from 'lucide-react';

import { useCommissions, useCommissionsStore } from '../features/commissions';
import { Button, Badge, Spinner } from '../ui/atoms';

// ── Dönem seçici yardımcısı ───────────────────────────────────
function buildPeriodOptions(count = 12) {
  const options = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  return options;
}

const STATUS_COLORS = {
  pending:  'amber',
  paid:     'green',
  canceled: 'gray',
};
const STATUS_LABELS = {
  pending:  'Bekliyor',
  paid:     'Ödendi',
  canceled: 'İptal',
};

export default function CommissionReport() {
  const { groupedByEmployee, loading, paying, markAsPaid } = useCommissions();

  const selectedPeriod    = useCommissionsStore((s) => s.selectedPeriod);
  const setSelectedPeriod = useCommissionsStore((s) => s.setSelectedPeriod);
  const statusFilter      = useCommissionsStore((s) => s.statusFilter);
  const setStatusFilter   = useCommissionsStore((s) => s.setStatusFilter);
  const expandedId        = useCommissionsStore((s) => s.expandedEmployeeId);
  const toggleExpand      = useCommissionsStore((s) => s.toggleExpandEmployee);
  const selectedIds       = useCommissionsStore((s) => s.selectedRecordIds);
  const toggleSelect      = useCommissionsStore((s) => s.toggleSelectRecord);
  const selectAll         = useCommissionsStore((s) => s.selectAllRecords);
  const clearSelection    = useCommissionsStore((s) => s.clearSelection);

  const periodOptions = buildPeriodOptions();

  // Tüm pending kayıt ID'leri (toplu seçim için)
  const allPendingIds = groupedByEmployee
    .flatMap((g) => g.records.filter((r) => r.status === 'pending').map((r) => r.id));

  const allSelected =
    allPendingIds.length > 0 && allPendingIds.every((id) => selectedIds.includes(id));

  const handleToggleAll = () =>
    allSelected ? clearSelection() : selectAll(allPendingIds);

  // Özet toplamlar
  const totals = groupedByEmployee.reduce(
    (acc, g) => {
      acc.total     += g.totalCommission;
      acc.pending   += g.records.filter((r) => r.status === 'pending').reduce((s, r) => s + r.totalCommission, 0);
      acc.paid      += g.records.filter((r) => r.status === 'paid').reduce((s, r) => s + r.totalCommission, 0);
      return acc;
    },
    { total: 0, pending: 0, paid: 0 }
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <ToastContainer position="top-right" />

      {/* ── Başlık ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="h-6 w-6 text-indigo-500" />
        <h1 className="text-2xl font-bold text-gray-800">Prim Raporu</h1>
      </div>

      {/* ── Filtre Çubuğu ──────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Dönem */}
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md
            focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {periodOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* Durum filtresi */}
        {['all', 'pending', 'paid', 'canceled'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
              statusFilter === s
                ? 'bg-indigo-500 text-white border-indigo-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {s === 'all' ? 'Tümü' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {/* ── Özet Kartlar ───────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Toplam Prim', value: totals.total, color: 'text-gray-800' },
          { label: 'Bekleyen',    value: totals.pending, color: 'text-amber-600' },
          { label: 'Ödenen',      value: totals.paid,    color: 'text-green-600' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className={`text-xl font-bold ${color}`}>
              ₺{value.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        ))}
      </div>

      {/* ── Toplu İşlem Araç Çubuğu ────────────────────────── */}
      {allPendingIds.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <button
            onClick={handleToggleAll}
            className="flex items-center gap-1.5 text-sm text-indigo-700 hover:text-indigo-800"
          >
            {allSelected
              ? <CheckSquare className="h-4 w-4" />
              : <Square className="h-4 w-4" />}
            Tümünü Seç ({allPendingIds.length})
          </button>

          {selectedIds.length > 0 && (
            <>
              <span className="text-xs text-indigo-500">|</span>
              <span className="text-xs text-indigo-600">
                {selectedIds.length} kayıt seçili
              </span>
              <Button
                variant="success"
                size="sm"
                loading={paying}
                onClick={markAsPaid}
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Ödendi İşaretle
              </Button>
            </>
          )}
        </div>
      )}

      {/* ── Çalışan Bazlı Liste ─────────────────────────────── */}
      {groupedByEmployee.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          Bu dönem için prim kaydı bulunamadı.
        </div>
      ) : (
        <div className="space-y-3">
          {groupedByEmployee.map(({ employee, records, totalCommission, pendingCount, paidCount }) => {
            const isExpanded = expandedId === employee.id;
            const pendingRecords = records.filter((r) => r.status === 'pending');

            return (
              <div key={employee.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Çalışan Satırı */}
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpand(employee.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm">
                      {employee.name?.[0]}{employee.surname?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        {employee.name} {employee.surname}
                      </p>
                      <p className="text-xs text-gray-500">
                        {records.length} adisyon · {pendingCount} bekliyor · {paidCount} ödendi
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <p className="font-bold text-gray-800">
                      ₺{totalCommission.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </p>
                    {isExpanded
                      ? <ChevronUp className="h-4 w-4 text-gray-400" />
                      : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>

                {/* Detay Satırları */}
                {isExpanded && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {records.map((rec) => (
                      <div
                        key={rec.id}
                        className="flex items-center gap-3 px-5 py-3 text-sm hover:bg-gray-50"
                      >
                        {/* Seçim kutusu (sadece pending) */}
                        <div className="w-5">
                          {rec.status === 'pending' ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleSelect(rec.id); }}
                              className="text-indigo-500 hover:text-indigo-600"
                            >
                              {selectedIds.includes(rec.id)
                                ? <CheckSquare className="h-4 w-4" />
                                : <Square className="h-4 w-4" />}
                            </button>
                          ) : (
                            <span className="w-4" />
                          )}
                        </div>

                        {/* Tarih */}
                        <span className="text-gray-500 w-24">{rec.billDate}</span>

                        {/* Hizmetler özeti */}
                        <div className="flex-1 text-gray-700 truncate">
                          {rec.services?.map((s) => s.serviceName).join(', ')}
                        </div>

                        {/* Prim tutarı */}
                        <span className="font-medium text-gray-800 w-20 text-right">
                          ₺{(rec.totalCommission || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </span>

                        {/* Durum */}
                        <div className="w-20 text-right">
                          <Badge color={STATUS_COLORS[rec.status] || 'gray'} size="xs">
                            {STATUS_LABELS[rec.status] || rec.status}
                          </Badge>
                        </div>
                      </div>
                    ))}

                    {/* Çalışan toplamı */}
                    <div className="flex justify-end px-5 py-2 bg-gray-50 text-sm font-semibold text-gray-700">
                      Toplam: ₺{totalCommission.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
